/**
 * Approved contacts on a client's cases: people the firm may talk to about a case who are not the
 * client, such as an OTR case manager (Hannah 7 Oct, Lloyd 9 Oct 2026).
 */

export interface CaseContactDto {
  CaseContact_guid: string;
  /** Null when the contact covers every case of the client. */
  Ticket_guid: string | null;
  CaseContact_Name: string;
  CaseContact_Role: string | null;
  CaseContact_Organization: string | null;
  CaseContact_Email: string | null;
  CaseContact_Phone: string | null;
  CaseContact_Note: string | null;
  CaseContact_ApprovedBy: string | null;
  CaseContact_CreatedAt: string;
}

export interface CaseContactInput {
  name: string;
  role?: string;
  organization?: string;
  email?: string;
  phone?: string;
  note?: string;
  /** Null = all of the client's cases. */
  Ticket_guid?: string | null;
}

async function failure(response: Response, fallback: string): Promise<Error> {
  const err = await response.json().catch(() => ({}));
  return new Error(err.error || err.message || fallback);
}

export default class CaseContactApi {
  private static base(): string {
    return `${process.env.API_HOST?.trim() || window.location.origin}/api/v1/case-contacts`;
  }

  /** The client's contacts, or only those that apply to one case when `ticketGuid` is given. */
  static async list(userGuid: string, ticketGuid?: string): Promise<CaseContactDto[]> {
    const params = new URLSearchParams({ User_guid: userGuid });
    if (ticketGuid) params.set('Ticket_guid', ticketGuid);
    const response = await fetch(`${CaseContactApi.base()}?${params}`, { credentials: 'include' });
    if (!response.ok) throw await failure(response, 'Failed to load the approved contacts');
    const body = await response.json();
    return body.contacts as CaseContactDto[];
  }

  static async create(userGuid: string, input: CaseContactInput): Promise<CaseContactDto> {
    const response = await fetch(CaseContactApi.base(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ User_guid: userGuid, ...input }),
    });
    if (!response.ok) throw await failure(response, 'Failed to add the contact');
    return response.json();
  }

  static async update(contactGuid: string, input: CaseContactInput): Promise<CaseContactDto> {
    const response = await fetch(`${CaseContactApi.base()}/${contactGuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input),
    });
    if (!response.ok) throw await failure(response, 'Failed to update the contact');
    return response.json();
  }

  static async remove(contactGuid: string): Promise<void> {
    const response = await fetch(`${CaseContactApi.base()}/${contactGuid}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) throw await failure(response, 'Failed to remove the contact');
  }
}
